import { LocalDateTime } from "@js-joda/core";
import classNames from "classnames";
import React from "react";
import {
  checkIfTimeSheetStrategy,
  ClockStrategy,
  getClockInStrategy,
  getClockOutStrategy,
} from "../clockInOutUtils";
import { ClockedInStatus, VisitInstanceDetails } from "../messages";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { PLAY_ICON, SQUARE_ICON } from "../utils/svgs_new";
import { getIsTaskCompleted } from "../utils/taskUtils";
import ClockedInButton from "./ClockedInButton";
import ClockedOutButton from "./ClockedOutButton";
import { AppButton } from "./Generic/AppButton";
import { AppIcon } from "./Generic/AppIcon";
import { isDefined } from "./Generic/IsDefined";

interface Props {
  visitDetails: VisitInstanceDetails;
  clockedInStatus: ClockedInStatus;
  onClickClockIn: (visitDetails: VisitInstanceDetails) => void;
  onClickClockOut: (visitDetails: VisitInstanceDetails) => void;
  onClickStartNow: (visitDetails: VisitInstanceDetails) => void;
}

const ClockInOutButtons = (props: Props) => {
  const clockInStrategy = getClockInStrategy(props.visitDetails);
  const clockOutStrategy = getClockOutStrategy(props.visitDetails);
  const isStartNowStrategy =
    clockInStrategy.visitType === "Present" || clockOutStrategy.visitType === "Present";
  const isTimesheetStrategy = checkIfTimeSheetStrategy(clockInStrategy, clockOutStrategy);

  const onClickClockIn = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    props.onClickClockIn(props.visitDetails);
  };

  const onClickClockOut = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    props.onClickClockOut(props.visitDetails);
  };

  const onClickStartNow = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    props.onClickStartNow(props.visitDetails);
  };

  if (isTimesheetStrategy) {
    return (
      <AppButton variant="filled" size="small" onClick={onClickClockIn}>
        <span>{translate(STRINGS.SUBMIT_DUTIES)}</span>
      </AppButton>
    );
  }

  if (isStartNowStrategy) {
    return (
      <AppButton variant="filled" size="small" onClick={onClickStartNow}>
        <span>
          {props.clockedInStatus.status?.clockInTime === undefined
            ? translate(STRINGS.START_NOW)
            : translate(STRINGS.CLOCK_OUT)}
        </span>
      </AppButton>
    );
  }

  return (
    <ClockInButtons
      visitDetails={props.visitDetails}
      clockInStrategy={clockInStrategy}
      clockOutStrategy={clockOutStrategy}
      onClickClockIn={onClickClockIn}
      onClickClockOut={onClickClockOut}
    />
  );
};

function ClockInButtons(props: {
  visitDetails: VisitInstanceDetails;
  clockInStrategy: ClockStrategy;
  clockOutStrategy: ClockStrategy;
  onClickClockIn: (e: React.MouseEvent<HTMLElement>) => void;
  onClickClockOut: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  const { clockInDateTime, clockOutDateTime } = getVisitClockTimes(props.visitDetails);

  const isClockInValidForGeoLocation =
    props.clockInStrategy.valid && props.clockInStrategy.strategy === "GeoLocation";
  const isClockOutValidForGeoLocation =
    props.clockOutStrategy.valid && props.clockOutStrategy.strategy === "GeoLocation";

  const clockInButton = (
    <AppButton
      variant={isClockInValidForGeoLocation ? "filled" : "outline"}
      size="small"
      onClick={props.onClickClockIn}
      className={classNames("ClockButton In", {
        disabled: !props.clockInStrategy.valid,
      })}
    >
      <AppIcon icon={PLAY_ICON} size={10} />
      <span>{translate(STRINGS.CLOCK_IN)}</span>
    </AppButton>
  );

  const clockOutButton = (
    <AppButton
      variant={isClockOutValidForGeoLocation ? "filled" : "outline"}
      size="small"
      onClick={props.onClickClockOut}
      className={classNames("ClockButton Out", {
        disabled: !props.clockOutStrategy.valid,
      })}
    >
      <AppIcon icon={SQUARE_ICON} size={10} />
      <span>{translate(STRINGS.CLOCK_OUT)}</span>
    </AppButton>
  );

  return (
    <>
      {getIsTaskCompleted(props.visitDetails, "CLOCK_IN") ? (
        <ClockedInButton
          clockInDateTime={clockInDateTime}
          manualClockTimeEditApproved={props.visitDetails.manualClockTimeEditApproved}
          onClick={props.onClickClockIn}
        />
      ) : (
        clockInButton
      )}
      {getIsTaskCompleted(props.visitDetails, "CLOCK_OUT") ? (
        <ClockedOutButton
          clockOutDateTime={clockOutDateTime}
          manualClockTimeEditApproved={props.visitDetails.manualClockTimeEditApproved}
          onClick={props.onClickClockOut}
        />
      ) : (
        clockOutButton
      )}
    </>
  );
}

function getVisitClockTimes(visitDetails: VisitInstanceDetails) {
  const clockInDateTime = getVisitClockInTime(visitDetails);
  const clockOutDateTime = getVisitClockOutTime(visitDetails);

  return {
    clockInDateTime: clockInDateTime,
    clockOutDateTime: clockOutDateTime,
  };
}

function getVisitClockInTime(visitDetails: VisitInstanceDetails) {
  if (isDefined(visitDetails.clockInTime)) {
    return visitDetails.clockInTime;
  } else if (visitDetails.checkInTime !== null) {
    return LocalDateTime.ofInstant(visitDetails.checkInTime);
  }
  return null;
}

function getVisitClockOutTime(visitDetails: VisitInstanceDetails) {
  if (isDefined(visitDetails.clockOutTime)) {
    return visitDetails.clockOutTime;
  } else if (visitDetails.checkOutTime !== null) {
    return LocalDateTime.ofInstant(visitDetails.checkOutTime);
  }
  return null;
}

export default ClockInOutButtons;
